import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { StruckSnapshotSchema } from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import { TagSchema } from "@redotech/redo-common-model-tags-common/tag";
import { checkoutShippingOptionSchema } from "@redotech/redo-conversion-checkout-shipping-common/checkout-shipping-option";
import { ServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  carrierToLabel,
  SelectedShippingRate,
  SelectedShippingRateSchema,
  servicesMatch,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { HazmatSchema } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import {
  ContentsType,
  NonDeliveryOption,
  OutboundShipmentTaxIdSchema,
  USPSExemptionCode,
} from "@redotech/redo-fulfillment-customs-common/customs";
import { CustomsDeclarationSchema } from "@redotech/redo-fulfillment-customs-common/customs-declaration";
import {
  FulfillmentOrderAddressSchema,
  VerifiedAddressSchema,
} from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { LockedLandedCostSchema } from "@redotech/redo-fulfillment-customs-common/rate-landed-cost";
import {
  ItemOrderBy,
  LearnedPresetKind,
} from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import {
  addWeight,
  convertWeight,
  MoneySchema,
  Weight,
  WeightSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { LearnedPresetSchema } from "@redotech/redo-fulfillment-parcel-common/learned-preset";
import {
  DEFAULT_REDO_PARCEL,
  getParcelWeightGrams,
  OutboundParcelCustomsSchema,
  OutboundParcelSchema,
  Parcel,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import { carrierAccountIdMatchesRate } from "@redotech/redo-fulfillment-rate-common/amazon-shipping-alias";
import {
  CanadaPostOption,
  IB_CARRIER_ACCOUNT_ID,
  IShipmentPaymentOption,
  OutboundRate,
  RateProvider,
  ShipmentMessageSchema,
  ShipmentPaymentOptionSchema,
  ShipmentRatesSchema,
  SpecialRateEligibility,
} from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { DhlEcommerceLabelOptionsSchema } from "@redotech/redo-fulfillment-rate-common/outbound-shipment";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { purchaseOrderIdFormat } from "@redotech/redo-inventory-id";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { filterTruthy } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { DateFromDateOrIsoStringSchema } from "./date-schema";
import { RedoShopifyDeliveryMethodType } from "./fulfillment-delivery-method-type";
import { OMSEventSchema } from "./fulfillment-group-events";
import { FulfillmentGroupStatus } from "./fulfillment-group-status";
import { FulfillmentHoldSchema } from "./fulfillment-hold";
import {
  LocationForMoveSchema,
  RedoFulfillmentLocationSchema,
} from "./fulfillment-location";
import { RedoShopifyFulfillmentOrderAction } from "./fulfillment-order-action";
import {
  expandLineItemsToPickableItems,
  FulfillmentOrderLineItem,
  FulfillmentOrderLineItemSchema,
  FulfillmentOrderLineItemSchema_,
  IFulfillmentOrderLineItem,
  LineItemLotSchema,
} from "./fulfillment-order-line-item";
import { RedoShopifyFulfillmentOrderRequestStatus } from "./fulfillment-order-request-status";
import { FulfillmentOrderIntegrationSourceSchema } from "./fulfillment-order-source";
import { RedoShopifyFulfillmentOrderStatus } from "./fulfillment-order-status";
import { PrintStatusSchema } from "./fulfillment-print-status";
import { RedoFulfillmentToteSchema } from "./fulfillment-tote";
import {
  DEFAULT_INVENTORY_SOURCE,
  InventorySourceSchema,
} from "./inventory-source";
import { MarketplaceFulfillmentDelayCriteriaSchema } from "./marketplace-fulfillment-settings";
import {
  IMarketplaceNotificationStatus,
  MarketplaceNotificationStatusSchema,
} from "./marketplace-notification-status";
import { IOMSUser, OMSUserSchema } from "./oms-user";
import { RedoShopifyOrderDisplayFinancialStatus } from "./order-display-financial-status";
import {
  RedoShopifyOrderDisputeInitiatedAs,
  RedoShopifyOrderDisputeStatus,
} from "./order-disputes";
import { OrderTransactionKind } from "./order-transaction-kind";
import {
  IRedoFulfillment,
  OmsInsuranceProvider,
  RecordedShipmentSchema,
  RedoFulfillmentSchema,
  ShipmentDetailsSchema,
  ShipmentPackage,
  ShipmentPackageSchema,
  ShipmentPackageSchema_,
  ShipmentTrackerSchema,
  ShipmentTrackerStatus,
} from "./outbound-shipment-label";
import {
  ItemParcelAssignmentsSchema,
  PackingParcelsSchema,
} from "./packing-parcels";
import { PlacedOnHoldSource } from "./placed-on-hold-source";
import {
  SelectedShippingRateSetBy,
  SelectedShippingRateSetByInfoSchema,
} from "./selected-shipping-rate-set-by";
import { ShipmentSummarySchema } from "./shipment-summary";
import {
  FulfillmentOrderSupportedActionSchema,
  IRisk,
  RiskSchema,
} from "./shopify-fulfillment-schemas";

// This defines the default range for competitive spread. By default, we
// will beat the opposing rate by a pseudo random value between 1 - 10 cents.
export const DEFAULT_COMP_SPREAD_BEAT_RATE_BY = 0.1;
export const DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY = 0.01;

// Do not change without approval from leadership
export const DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES = [
  { carriers: [Carriers.DHL_ECS], minBeatRateBy: 0.1, maxBeatRateBy: 0.3 },
];

export enum ScanAction {
  SCAN = "scan",
  UNSCAN = "unscan",
}

const FulfillmentGroupCustomsSchema_ = z.object({
  declarations: z
    .array(CustomsDeclarationSchema)
    .meta({ privacy: privacy.structured }),
  overwritten: z.boolean().meta({ privacy: privacy.nonPii }),
});
export const FulfillmentGroupCustomsSchema: z.ZodType<FulfillmentGroupCustoms> =
  FulfillmentGroupCustomsSchema_;
export interface FulfillmentGroupCustoms extends z.infer<
  typeof FulfillmentGroupCustomsSchema_
> {}

export const LabelErrorSchema = z.object({
  message: z.string().meta({ privacy: privacy.systemErrorText }),
});
export type LabelError = z.infer<typeof LabelErrorSchema>;

const OrderTransactionSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  formattedGateway: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  gateway: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  paymentDetails: z
    .object({
      company: z
        .string()
        .meta({ privacy: privacy.company_name.thirdParty })
        .nullish(),
      number: z.string().meta({ privacy: privacy.paymentInstrument }).nullish(),
      lastFour: z.string().meta({ privacy: privacy.cardLast4 }).nullish(),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
  amount: MoneySchema.meta({ privacy: privacy.structured }),
  parentTransactionId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  kind: createEnum(OrderTransactionKind, {
    enumName: "OrderTransactionKind",
  }).meta({ privacy: privacy.nonPii }),
});
export const OrderTransactionSchema: z.ZodType<OrderTransaction> =
  OrderTransactionSchema_;
export interface OrderTransaction extends z.infer<
  typeof OrderTransactionSchema_
> {}

const OrderDisputeSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  status: createEnum(RedoShopifyOrderDisputeStatus, {
    enumName: "RedoShopifyOrderDisputeStatus",
  }).meta({ privacy: privacy.nonPii }),
  initiatedAs: createEnum(RedoShopifyOrderDisputeInitiatedAs, {
    enumName: "RedoShopifyOrderDisputeInitiatedAs",
  }).meta({ privacy: privacy.nonPii }),
});
export const OrderDisputeSchema: z.ZodType<OrderDispute> = OrderDisputeSchema_;
export interface OrderDispute extends z.infer<typeof OrderDisputeSchema_> {}

const FulfillmentOrderCustomerSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  name: z.string().meta({ privacy: privacy.person_name.customer }),
  username: z
    .string()
    .meta({ privacy: privacy.socialHandle.customer })
    .nullish(),
  tags: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  numOrders: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  email: z.string().meta({ privacy: privacy.email.customer }).nullish(),
  phone: z.string().meta({ privacy: privacy.phone.customer }).nullish(),
  note: z.string().meta({ privacy: privacy.text.customer }).nullish(),
  localizations: z
    .array(z.string().meta({ privacy: privacy.untypedPayload }))
    .nullish()
    .meta({ privacy: privacy.untypedPayload }),
});

export const FulfillmentOrderCustomerSchema: z.ZodType<FulfillmentOrderCustomer> =
  FulfillmentOrderCustomerSchema_;

//https://lorefnon.me/2023/11/28/fixing-inferred-types-exceeding-serializable-length/
export interface FulfillmentOrderCustomer extends z.infer<
  typeof FulfillmentOrderCustomerSchema_
> {}

/**
 * Shopify's LocalizationExtensionKey enum values.
 * These identify credentials provided by the customer at checkout.
 * Source: Shopify Admin GraphQL introspection of LocalizationExtensionKey.
 */
export enum ShopifyLocalizationExtensionKey {
  SHIPPING_CREDENTIAL_BR = "SHIPPING_CREDENTIAL_BR",
  SHIPPING_CREDENTIAL_CL = "SHIPPING_CREDENTIAL_CL",
  SHIPPING_CREDENTIAL_CN = "SHIPPING_CREDENTIAL_CN",
  SHIPPING_CREDENTIAL_CO = "SHIPPING_CREDENTIAL_CO",
  SHIPPING_CREDENTIAL_CR = "SHIPPING_CREDENTIAL_CR",
  SHIPPING_CREDENTIAL_EC = "SHIPPING_CREDENTIAL_EC",
  SHIPPING_CREDENTIAL_ES = "SHIPPING_CREDENTIAL_ES",
  SHIPPING_CREDENTIAL_GT = "SHIPPING_CREDENTIAL_GT",
  SHIPPING_CREDENTIAL_ID = "SHIPPING_CREDENTIAL_ID",
  SHIPPING_CREDENTIAL_KR = "SHIPPING_CREDENTIAL_KR",
  SHIPPING_CREDENTIAL_MX = "SHIPPING_CREDENTIAL_MX",
  SHIPPING_CREDENTIAL_MY = "SHIPPING_CREDENTIAL_MY",
  SHIPPING_CREDENTIAL_PE = "SHIPPING_CREDENTIAL_PE",
  SHIPPING_CREDENTIAL_PT = "SHIPPING_CREDENTIAL_PT",
  SHIPPING_CREDENTIAL_PY = "SHIPPING_CREDENTIAL_PY",
  SHIPPING_CREDENTIAL_TR = "SHIPPING_CREDENTIAL_TR",
  SHIPPING_CREDENTIAL_TW = "SHIPPING_CREDENTIAL_TW",
  SHIPPING_CREDENTIAL_TYPE_CO = "SHIPPING_CREDENTIAL_TYPE_CO",
  TAX_CREDENTIAL_BR = "TAX_CREDENTIAL_BR",
  TAX_CREDENTIAL_CL = "TAX_CREDENTIAL_CL",
  TAX_CREDENTIAL_CO = "TAX_CREDENTIAL_CO",
  TAX_CREDENTIAL_CR = "TAX_CREDENTIAL_CR",
  TAX_CREDENTIAL_EC = "TAX_CREDENTIAL_EC",
  TAX_CREDENTIAL_ES = "TAX_CREDENTIAL_ES",
  TAX_CREDENTIAL_GT = "TAX_CREDENTIAL_GT",
  TAX_CREDENTIAL_ID = "TAX_CREDENTIAL_ID",
  TAX_CREDENTIAL_IT = "TAX_CREDENTIAL_IT",
  TAX_CREDENTIAL_MX = "TAX_CREDENTIAL_MX",
  TAX_CREDENTIAL_MY = "TAX_CREDENTIAL_MY",
  TAX_CREDENTIAL_PE = "TAX_CREDENTIAL_PE",
  TAX_CREDENTIAL_PT = "TAX_CREDENTIAL_PT",
  TAX_CREDENTIAL_PY = "TAX_CREDENTIAL_PY",
  TAX_CREDENTIAL_TR = "TAX_CREDENTIAL_TR",
  TAX_CREDENTIAL_TYPE_CO = "TAX_CREDENTIAL_TYPE_CO",
  TAX_CREDENTIAL_TYPE_MX = "TAX_CREDENTIAL_TYPE_MX",
  TAX_CREDENTIAL_USE_MX = "TAX_CREDENTIAL_USE_MX",
  TAX_EMAIL_IT = "TAX_EMAIL_IT",
}

export const CustomerTaxCredentialSchema = z.object({
  /** The Shopify LocalizationExtensionKey identifying the tax credential type */
  key: createEnum(ShopifyLocalizationExtensionKey, {
    enumName: "ShopifyLocalizationExtensionKey",
  }).meta({ privacy: privacy.nonPii }),
  /** The actual tax credential value (e.g. the RFC number) */
  value: z.string().meta({ privacy: privacy.id.government }),
  /** The Shopify CountryCode associated with this credential */
  countryCode: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});
export type CustomerTaxCredential = z.infer<typeof CustomerTaxCredentialSchema>;

const FulfillmentOrderOrderSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  redoOrderId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  fullyPaid: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  displayFinancialStatus: createEnum(RedoShopifyOrderDisplayFinancialStatus, {
    enumName: "RedoShopifyOrderDisplayFinancialStatus",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  discountCodes: z
    .array(z.string().meta({ privacy: privacy.id.capability }))
    .nullish()
    .meta({ privacy: privacy.untypedPayload }),
  name: z.string().meta({ privacy: privacy.nonPii }),
  secondaryOrderName: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  risk: RiskSchema.nullish().meta({ privacy: privacy.structured }),
  tags: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  note: z.string().meta({ privacy: privacy.text.customer }),
  customAttributes: z
    .array(
      z.object({
        key: z.string().meta({ privacy: privacy.nonPii }),
        value: z.string().meta({ privacy: privacy.text.customer }).nullish(),
      }),
    )
    .nullish()
    .meta({ privacy: privacy.structured }),
  createdAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
  shippingCost: MoneySchema.meta({ privacy: privacy.structured }),
  shippingCostPresented: MoneySchema.nullish().meta({
    privacy: privacy.structured,
  }),
  shippingTaxLines: z
    .array(
      z
        .object({ priceSet: MoneySchema.meta({ privacy: privacy.structured }) })
        .meta({ privacy: privacy.structured }),
    )
    .nullish()
    .meta({ privacy: privacy.structured }),
  closed: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  closedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  salesChannel: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  channelInformation: z
    .object({
      app: z
        .object({
          developerName: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        })
        .meta({ privacy: privacy.structured }),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
  internalNote: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
  packageProtected: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  customDate1: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  customDate2: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  customDate3: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  customString1: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
  customString2: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
  customString3: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
  customNumber1: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  customNumber2: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  customNumber3: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  customAttributeDate1: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  customAttributeDate2: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  customAttributeDate3: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  customAttributeString1: z
    .string()
    .meta({ privacy: privacy.text.merchant })
    .nullish(),
  customAttributeString2: z
    .string()
    .meta({ privacy: privacy.text.merchant })
    .nullish(),
  customAttributeString3: z
    .string()
    .meta({ privacy: privacy.text.merchant })
    .nullish(),
  customAttributeNumber1: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  customAttributeNumber2: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  customAttributeNumber3: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  disputes: z
    .array(OrderDisputeSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  /** Customer-provided tax credentials from Shopify localization extensions (e.g. Mexico RFC) */
  customerTaxCredentials: z
    .array(CustomerTaxCredentialSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  inventorySource: InventorySourceSchema.default(DEFAULT_INVENTORY_SOURCE).meta(
    { privacy: privacy.nonPii },
  ),
});
export const FulfillmentOrderOrderSchema: z.ZodType<FulfillmentOrderOrder> =
  FulfillmentOrderOrderSchema_;
export interface FulfillmentOrderOrder extends z.infer<
  typeof FulfillmentOrderOrderSchema_
> {}

const RedoFulfillmentServiceOrderSchema_ = z.object({
  externalLocationPoolId: z.string().meta({ privacy: privacy.nonPii }),
  externalLocationId: z.string().meta({ privacy: privacy.nonPii }),
  fulfillmentServiceTeamId: z.string().meta({ privacy: privacy.nonPii }),
});
export const RedoFulfillmentServiceOrderSchema: z.ZodType<RedoFulfillmentServiceOrder> =
  RedoFulfillmentServiceOrderSchema_;
export interface RedoFulfillmentServiceOrder extends z.infer<
  typeof RedoFulfillmentServiceOrderSchema_
> {}

const FulfillmentOrderSchema_ = z.object({
  source: FulfillmentOrderIntegrationSourceSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  provider: createEnum(Provider, { enumName: "Provider" }).meta({
    privacy: privacy.nonPii,
  }),
  externalId: z.string().meta({ privacy: privacy.nonPii }), // SHOULD BE GLOBALLY UNIQUE
  externalSearchId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  subsidiaryId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  billingAddress: FulfillmentOrderAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  originAddress: FulfillmentOrderAddressSchema.meta({
    privacy: privacy.structured,
  }),
  verifiedOriginAddress: VerifiedAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  deliveryMethod: z
    .object({
      name: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      code: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      kind: createEnum(RedoShopifyDeliveryMethodType, {
        enumName: "RedoShopifyDeliveryMethodType",
      }).meta({ privacy: privacy.nonPii }),
      instructions: z
        .string()
        .meta({ privacy: privacy.text.customer })
        .nullish(),
      phone: z.string().meta({ privacy: privacy.phone.customer }).nullish(),
      selectedRedoCheckoutShippingOptions: z
        .array(checkoutShippingOptionSchema)
        .nullish()
        .meta({ privacy: privacy.structured }),
      merchantCheckoutLandedCost: z
        .object({
          providerOrderId: z.string().meta({ privacy: privacy.nonPii }),
        })
        .nullish()
        .meta({ privacy: privacy.structured }),
    })
    .meta({ privacy: privacy.structured }),
  destinationAddress: FulfillmentOrderAddressSchema.meta({
    privacy: privacy.structured,
  }),
  verifiedDestinationAddress: VerifiedAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  createdAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
  updatedAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
  fulfillAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  fulfillBy: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  /**
   * WARNING: Line items may contain sub items from Bill of Materials (BOM) expansion.
   * If the use case for accessing the line items is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use `expandLineItemsToPickableItems(lineItems)` to get only the leaf node items.
   * In most cases (splitting, table filtering, etc) - you probably want to use the top level line items - the actual line items that were ordered.
   */
  lineItems: z
    .array(FulfillmentOrderLineItemSchema as IFulfillmentOrderLineItem)
    .meta({ privacy: privacy.structured }),
  transactions: z
    .array(OrderTransactionSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  locationsForMove: z
    .array(LocationForMoveSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  supportedActions: z
    .array(FulfillmentOrderSupportedActionSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  order: FulfillmentOrderOrderSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  customer: FulfillmentOrderCustomerSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  requestStatus: createEnum(RedoShopifyFulfillmentOrderRequestStatus, {
    enumName: "RedoShopifyFulfillmentOrderRequestStatus",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  status: createEnum(RedoShopifyFulfillmentOrderStatus, {
    enumName: "RedoShopifyFulfillmentOrderStatus",
  }).meta({ privacy: privacy.nonPii }),
  redoFulfillmentService: RedoFulfillmentServiceOrderSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  /** Reference to an inventory transfer that this fulfillment order was created from */
  transferId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  /** Fulfillment holds synced from the provider. Read-only — written only by the sync. */
  holds: z
    .array(FulfillmentHoldSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  raw: z.unknown().nullish().meta({ privacy: privacy.untypedPayload }),
});
export const FulfillmentOrderSchema: z.ZodType<FulfillmentOrder> =
  FulfillmentOrderSchema_;
export interface FulfillmentOrder extends z.infer<
  typeof FulfillmentOrderSchema_
> {}

const ShippingCostBreakdownSchema = z
  .object({
    fulfillmentOrdersBreakdown: z
      .array(
        z
          .object({
            labelCost: z.string().meta({ privacy: privacy.nonPii }),
            markup: z
              .object({
                percentage: z
                  .string()
                  .meta({ privacy: privacy.nonPii })
                  .optional(),
                amount: z.string().meta({ privacy: privacy.nonPii }).optional(),
              })
              .optional()
              .meta({ privacy: privacy.structured }),
          })
          .meta({ privacy: privacy.structured }),
      )
      .optional()
      .meta({ privacy: privacy.structured }),
  })
  .merge(MoneySchema);
export interface ShippingCostBreakdown extends z.infer<
  typeof ShippingCostBreakdownSchema
> {}

export enum OverrideAddressSourceType {
  AUTO_MERGE = "auto_merge",
  MERGE = "merge",
  MANUAL = "manual",
}

const FulfillmentGroupAddressesSchema_ = z.object({
  address: FulfillmentOrderAddressSchema.meta({ privacy: privacy.structured }),
  verifiedAddress: VerifiedAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  sourceType: createEnum(OverrideAddressSourceType, {
    enumName: "OverrideAddressSourceType",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});
export const FulfillmentGroupAddressesSchema: z.ZodType<FulfillmentGroupAddresses> =
  FulfillmentGroupAddressesSchema_;
export interface FulfillmentGroupAddresses extends z.infer<
  typeof FulfillmentGroupAddressesSchema_
> {}

export enum IntegrationType {
  TRACKSTAR = "trackstar",
}

export function isInventoryIntegration(type: IntegrationType): boolean {
  switch (type) {
    case IntegrationType.TRACKSTAR:
      return true;
    default:
      return assertNever(type);
  }
}

export const BinSchema = z.object({
  bin: z.string().meta({ privacy: privacy.nonPii }),
  quantity: z.number().meta({ privacy: privacy.nonPii }).nullish(),
});

export type Bin = z.infer<typeof BinSchema>;

const LineItemLocationSchema_ = z.object({
  sku: z.string().meta({ privacy: privacy.nonPii }),
  bins: z.array(BinSchema).nullish().meta({ privacy: privacy.structured }),
});
export const LineItemLocationSchema: z.ZodType<LineItemLocation> =
  LineItemLocationSchema_;
export interface LineItemLocation extends z.infer<
  typeof LineItemLocationSchema_
> {}

const TrackstarIntegrationReferenceSchema_ = z.object({
  integrationType: z
    .literal(IntegrationType.TRACKSTAR)
    .meta({ privacy: privacy.nonPii }),
  shipmentFulfillmentPairs: z
    .array(
      z
        .object({
          fulfillmentOrderId: z.string().meta({ privacy: privacy.nonPii }),
          shipmentId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          lineItemLocations: z
            .array(LineItemLocationSchema)
            .nullish()
            .meta({ privacy: privacy.structured }),
        })
        .meta({ privacy: privacy.structured }),
    )
    .meta({ privacy: privacy.structured }),
});
export const TrackstarIntegrationReferenceSchema: z.ZodType<TrackstarIntegrationReference> =
  TrackstarIntegrationReferenceSchema_;

export enum FulfilledBy {
  REDO = "Redo",
  EXTERNAL = "External",
}

export enum ProductRecommendationsSource {
  AI = "ai",
  FALLBACK = "fallback",
  NONE = "none",
}

const FulfillmentInfoSchema_ = z.object({
  carrier: createEnum(Carriers, { enumName: "Carriers" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  service: ServiceSchema.nullish().meta({ privacy: privacy.structured }),
  trackingCode: z.string().meta({ privacy: privacy.id.tracking }).nullish(),
  shipDate: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  numericRate: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  // Inlined (not MoneySchema) so this persisted field stays DDL-extractable.
  rateMoney: z
    .object({
      amount: z.number().meta({ privacy: privacy.nonPii }),
      currency: createEnum(Currency, { enumName: "Currency" }).meta({
        privacy: privacy.nonPii,
      }),
      struck: StruckSnapshotSchema.optional().meta({
        privacy: privacy.structured,
      }),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
  estimatedDeliveryDays: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  tracker: ShipmentTrackerSchema.nullish().meta({
    privacy: privacy.structured,
  }),
});
export const FulfillmentInfoSchema: z.ZodType<FulfillmentInfo> =
  FulfillmentInfoSchema_;
export interface FulfillmentInfo extends z.infer<
  typeof FulfillmentInfoSchema_
> {}

const FulfillmentGroupSummarySchema_ = z.object({
  /**
   * WARNING: If the use case is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use `expandedItemQuantity` instead.
   * In most cases (order totals, reporting, etc) - you probably want to use this field - the total quantity of items that were ordered.
   */
  totalItemQuantity: z.number().meta({ privacy: privacy.nonPii }),
  /**
   * The number of items after Bill of Materials (BOM) expansion - only counts leaf-node items (actual components to ship/pick).
   * If the use case is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use this field.
   * In most cases (order totals, reporting, etc) - you probably want to use `totalItemQuantity` - the total quantity of items that were ordered.
   * Falls back to totalItemQuantity if no BOM expansion exists.
   */
  expandedItemQuantity: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  totalSkuCount: z.number().meta({ privacy: privacy.nonPii }),
  totalLineItemCount: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  groupedProductCount: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  groupedProductWithAttributesCount: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  totalWeightGrams: z.number().meta({ privacy: privacy.nonPii }),
  totalWeightWithParcelGrams: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  displayWeightGrams: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  totalPrice: MoneySchema.meta({ privacy: privacy.structured }),
  totalDiscountedPrice: MoneySchema.nullish().meta({
    privacy: privacy.structured,
  }),
  totalPriceInt: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  totalTax: MoneySchema.meta({ privacy: privacy.structured }),
  totalTaxInt: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  totalShippingCost: ShippingCostBreakdownSchema.meta({
    privacy: privacy.structured,
  }),
  totalShippingCostInt: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  /**
   * totalPrice + totalShippingCost + totalTax. See the calculation in
   * calculateSummary for caveats about orders split across multiple
   * fulfillment groups.
   */
  totalPriceWithShippingAndTaxes: MoneySchema.nullish().meta({
    privacy: privacy.structured,
  }),
  totalPriceWithShippingAndTaxesInt: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  providerCreatedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }),
  providerUpdatedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }),
  updatedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  deliveryMethod: createEnum(RedoShopifyDeliveryMethodType, {
    enumName: "RedoShopifyDeliveryMethodType",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  originAddress: FulfillmentOrderAddressSchema.meta({
    privacy: privacy.structured,
  }),
  verifiedOriginAddress: VerifiedAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  destinationAddress: FulfillmentOrderAddressSchema.meta({
    privacy: privacy.structured,
  }),
  verifiedDestinationAddress: VerifiedAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  billingAddress: FulfillmentOrderAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  customer: FulfillmentOrderCustomerSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  orderCreatedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }),
  lineItemLocations: z
    .array(LineItemLocationSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  fulfillmentInfo: FulfillmentInfoSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  batchSuggestionKey: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  fulfilledBy: createEnum(FulfilledBy, { enumName: "FulfilledBy" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  fulfillmentOrderCount: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  ratesOutdated: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  checkoutPromisedDeliveryDate: z
    .date()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  needsTrackingSync: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  hasMissingBins: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  returnShipmentOverrides: z
    .object({
      shopperAddress: FulfillmentOrderAddressSchema.nullish().meta({
        privacy: privacy.structured,
      }),
      merchantAddress: FulfillmentOrderAddressSchema.nullish().meta({
        privacy: privacy.structured,
      }),
      parcel: OutboundParcelSchema.nullish().meta({
        privacy: privacy.structured,
      }),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
  totalDimValue: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  usedLearning: z
    .object({
      [LearnedPresetKind.CARRIER_SERVICE_LEVEL]: z
        .boolean()
        .meta({ privacy: privacy.nonPii }),
      [LearnedPresetKind.PARCEL]: z.boolean().meta({ privacy: privacy.nonPii }),
      [LearnedPresetKind.WEIGHT]: z.boolean().meta({ privacy: privacy.nonPii }),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
  /**
   * Denormalized product info including BOM-expanded product IDs.
   * Needed because top-level lineItems only contain parent product IDs —
   * BOM child product IDs are nested in billOfMaterialsInfos recursively.
   * This means we can't add an index on that directly, so we denormalize it here.
   */
  productInfo: z
    .object({
      productIds: z
        .array(z.string().meta({ privacy: privacy.nonPii }))
        .meta({ privacy: privacy.structured }),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
  redoSplit: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  startZoneId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
});
export const FulfillmentGroupSummarySchema: z.ZodType<FulfillmentGroupSummary> =
  FulfillmentGroupSummarySchema_;
export interface FulfillmentGroupSummary extends z.infer<
  typeof FulfillmentGroupSummarySchema_
> {}

export enum SortingCriteria {
  FASTEST = "FASTEST",
  CHEAPEST = "CHEAPEST",
  CHEAPEST_WITHIN_DELIVERY_DAYS = "CHEAPEST_WITHIN_DELIVERY_DAYS",
  CHEAPEST_WITHIN_BUSINESS_DAYS = "CHEAPEST_WITHIN_BUSINESS_DAYS",
  CHEAPEST_WITHIN_DELIVERABLE_DAYS = "CHEAPEST_WITHIN_DELIVERABLE_DAYS",
}

export const cheapestWithinDaysSortingCriteria = [
  SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS,
  SortingCriteria.CHEAPEST_WITHIN_BUSINESS_DAYS,
  SortingCriteria.CHEAPEST_WITHIN_DELIVERABLE_DAYS,
] as const;
export type CheapestWithinDaysSortingCriteria =
  (typeof cheapestWithinDaysSortingCriteria)[number];

export function isCheapestWithinDaysSortingCriteria(
  sortingCriteria: SortingCriteria | null | undefined,
): sortingCriteria is CheapestWithinDaysSortingCriteria {
  switch (sortingCriteria) {
    case SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS:
    case SortingCriteria.CHEAPEST_WITHIN_BUSINESS_DAYS:
    case SortingCriteria.CHEAPEST_WITHIN_DELIVERABLE_DAYS:
      return true;
    case SortingCriteria.CHEAPEST:
    case SortingCriteria.FASTEST:
    case null:
    case undefined:
      return false;
    default:
      return assertNever(sortingCriteria);
  }
}

const RateSelectionCriteria_ = z.object({
  ratesToChooseFrom: z
    .array(SelectedShippingRateSchema)
    .meta({ privacy: privacy.structured }),
  sortingCriteria: createEnum(SortingCriteria, {
    enumName: "SortingCriteria",
  }).meta({ privacy: privacy.nonPii }),
  ratesToPrioritize: z
    .array(SelectedShippingRateSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  maxPriorityPriceDifference: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  targetDeliveryDays: z.number().meta({ privacy: privacy.nonPii }).nullish(),
});
export const RateSelectionCriteria: z.ZodType<RateSelectionCriteria> =
  RateSelectionCriteria_;
export interface RateSelectionCriteria extends z.infer<
  typeof RateSelectionCriteria_
> {}

export enum InsuranceSelectionType {
  PERCENTAGE = "percentage",
  FIXED = "fixed",
}

const InsuranceSelectionCriteria_ = z.discriminatedUnion("type", [
  z.object({
    type: z
      .literal(InsuranceSelectionType.PERCENTAGE)
      .meta({ privacy: privacy.nonPii }),
    percentage: z.number().meta({ privacy: privacy.nonPii }),
  }),
  z.object({
    type: z
      .literal(InsuranceSelectionType.FIXED)
      .meta({ privacy: privacy.nonPii }),
    amount: z.number().meta({ privacy: privacy.nonPii }),
  }),
]);
export const InsuranceSelectionCriteria: z.ZodType<InsuranceSelectionCriteria> =
  InsuranceSelectionCriteria_;
export type InsuranceSelectionCriteria = z.infer<
  typeof InsuranceSelectionCriteria_
>;

export interface TrackstarIntegrationReference extends z.infer<
  typeof TrackstarIntegrationReferenceSchema_
> {}

const IntegrationReferenceSchema_ = z.discriminatedUnion("integrationType", [
  TrackstarIntegrationReferenceSchema_,
]);
export const IntegrationReferenceSchema: z.ZodType<IntegrationReference> =
  IntegrationReferenceSchema_;
export type IntegrationReference = z.infer<typeof IntegrationReferenceSchema_>;

export enum ReturnShipmentStatus {
  RETURN_LABEL_PRE_CHARGE = "RETURN_LABEL_PRE_CHARGE",
  RETURN_LABEL_CHARGED = "RETURN_LABEL_CHARGED",
  RETURN_LABEL_VOIDED = "RETURN_LABEL_VOIDED",
}

export enum AddressFixedBy {
  ADDRESS_COMPARISON_AGENT = "ADDRESS_COMPARISON_AGENT",
  ADDRESS_FIXING_AGENT = "ADDRESS_FIXING_AGENT",
  CUSTOMER_AFTER_EMAIL = "CUSTOMER_AFTER_EMAIL",
}

const GroupSelectedRateSchema_ = SelectedShippingRateSchema.extend({
  rate: z.number().meta({ privacy: privacy.nonPii }).nullish(),
});
export const GroupSelectedRateSchema: z.ZodType<GroupSelectedRate> =
  GroupSelectedRateSchema_;
export interface GroupSelectedRate extends z.infer<
  typeof GroupSelectedRateSchema_
> {}

export enum FulfillmentGroupPickStatus {
  PICKED = "PICKED",
  PROBLEM = "PROBLEM",
  IN_PROGRESS = "IN_PROGRESS",
  // Set on FGs that have at least one HANDED_OFF / ACTIVE continuation
  // session waiting at a downstream zone. Distinguishes "still being
  // worked through the zone sequence" from a fresh queue entry, so the
  // FG-list dashboards can surface the in-flight handoff state. Cleared
  // back to PICKED when the last continuation in the chain finishes.
  HANDED_OFF = "HANDED_OFF",
}

const PickSessionItemStatusSchema_ = z.object({
  fulfillmentOrderLineItemExternalId: z
    .string()
    .meta({ privacy: privacy.nonPii }),
  quantityPicked: z.number().meta({ privacy: privacy.nonPii }),
});
export interface PickSessionItemStatus extends z.infer<
  typeof PickSessionItemStatusSchema_
> {}
const PickSessionItemStatusSchema: z.ZodType<PickSessionItemStatus> =
  PickSessionItemStatusSchema_;

const HoldsPlacedByInfoSchema_ = z.object({
  source: createEnum(PlacedOnHoldSource, { enumName: "PlacedOnHoldSource" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  placedByUserId: zExt.objectId().meta({ privacy: privacy.nonPii }).nullish(),
  placedByAutomationId: zExt
    .objectId()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  placedByAutomationName: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  releasedByUserId: zExt.objectId().meta({ privacy: privacy.nonPii }).nullish(),
});
export const HoldsPlacedByInfoSchema: z.ZodType<HoldsPlacedByInfo> =
  HoldsPlacedByInfoSchema_;
export interface HoldsPlacedByInfo extends z.infer<
  typeof HoldsPlacedByInfoSchema_
> {}

const fulfillmentGroupSchema_ = z
  .object({
    _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
    numericId: z.number().meta({ privacy: privacy.nonPii }).nullish(), // TODO @samuel: make non-nullish once all groups have a numericId and new groups are created with a numericId
    team: zExt.objectId().meta({ privacy: privacy.nonPii }),
    teamName: z.string().meta({ privacy: privacy.nonPii }),
    assignedFulfillmentService: zExt
      .objectId()
      .meta({ privacy: privacy.nonPii }),
    summary: FulfillmentGroupSummarySchema.meta({
      privacy: privacy.structured,
    }),
    slaFulfillmentDate: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    batchId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    prettyBatchId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    voidedBatchId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    status: createEnum(FulfillmentGroupStatus, {
      enumName: "FulfillmentGroupStatus",
    }).meta({ privacy: privacy.nonPii }),
    fulfillmentOrders: z
      .array(FulfillmentOrderSchema.meta({ privacy: privacy.structured }))
      .meta({ privacy: privacy.structured }),
    shipmentRates: ShipmentRatesSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    // We have three weights that matter: totalWeightWithParcel, overrideWeight, and displayWeight
    // Total weight with parcel is the weight of the items plus the parcel, calculated on creation/update. We keep this in the case of "reset weight" in the UI
    // Override weight is only set when the user manually overrides the weight. It may be null
    // Display weight is the "summary" weight (not actually in the summary, but should be). It's the override weight if it exists, or the total weight with parcel if it doesn't. It's what we show to the user and search/filter on
    /** @deprecated Use selectedParcels instead */
    overrideWeight: WeightSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    displayWeightGrams: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    /** @deprecated Use selectedParcels instead */
    selectedParcel: OutboundParcelSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    // Must be nullish before the migration. The transform will make other things infer non-nullish.
    selectedParcels: z
      .array(OutboundParcelSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    // selectedParcels: z.array(ParcelSchema).refine((parcels) => parcels.length > 0, {
    //   message: "At least one parcel is required",
    // }),
    selectedShippingRate: GroupSelectedRateSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    selectedShippingRateSetBy: createEnum(SelectedShippingRateSetBy, {
      enumName: "SelectedShippingRateSetBy",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    selectedShippingRateSetByInfo:
      SelectedShippingRateSetByInfoSchema.nullish().meta({
        privacy: privacy.structured,
      }),
    rateSelectionCriteria: RateSelectionCriteria.nullish().meta({
      privacy: privacy.structured,
    }),
    insuranceSelectionCriteria: InsuranceSelectionCriteria.nullish().meta({
      privacy: privacy.structured,
    }),
    createdAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    updatedAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    tags: z.array(TagSchema).nullish().meta({ privacy: privacy.structured }),
    noteToBuyer: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
    noteFromBuyer: z
      .string()
      .meta({ privacy: privacy.text.customer })
      .nullish(),
    internalNote: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
    assignedUserId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    // Not meaningful yet, needs a backfill
    shipmentDetails: ShipmentDetailsSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    // Shipment and additionalShipments will be deprecated soon, once we backfill shipments
    shipment: ShipmentPackageSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    additionalShipments: z
      .array(ShipmentPackageSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    fulfillments: z
      .array(
        (RedoFulfillmentSchema as IRedoFulfillment).meta({
          privacy: privacy.structured,
        }),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
    printStatus: PrintStatusSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    learnedPresets: z
      .array(LearnedPresetSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    ratesPendingSince: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    signatureRequired: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    carbonNeutral: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    canadaPostOption: createEnum(CanadaPostOption, {
      enumName: "CanadaPostOption",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    dhlEcommerceLabelOptions: DhlEcommerceLabelOptionsSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    // Required by UPS - Prevents rerouting of a package to an alternative address such as an Access Point
    directDeliveryOnly: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    // Override the residential flag on the destination address
    overrideResidentialAddress: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    adultSignatureRequired: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    /** @deprecated Stored at the parcel level instead */
    includeInsurance: z.boolean().meta({ privacy: privacy.nonPii }).nullish(), // if this is true but insuredAmount is not set, we insure the whole package
    /** @deprecated Stored at the parcel level instead */
    insuredAmount: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    specialRateEligibility: createEnum(SpecialRateEligibility, {
      enumName: "SpecialRateEligibility",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    overrideDestinationAddresses:
      FulfillmentGroupAddressesSchema.nullish().meta({
        privacy: privacy.structured,
      }),
    addressManuallyUpdated: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .default(false),
    overrideOriginAddress: z
      .object({
        addresses: FulfillmentGroupAddressesSchema.nullish().meta({
          privacy: privacy.structured,
        }),
        locationId: zExt.objectId().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    overrideDeliveryMethod: createEnum(RedoShopifyDeliveryMethodType, {
      enumName: "RedoShopifyDeliveryMethodType",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    customs: FulfillmentGroupCustomsSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    contentsType: createEnum(ContentsType, { enumName: "ContentsType" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    contentsExplanation: z
      .string()
      .meta({ privacy: privacy.text.merchant })
      .nullish(),
    additionalCustomsComments: z
      .string()
      .meta({ privacy: privacy.text.merchant })
      .nullish(),
    nonDeliveryOption: createEnum(NonDeliveryOption, {
      enumName: "NonDeliveryOption",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    uspsExemptionCode: createEnum(USPSExemptionCode, {
      enumName: "USPSExemptionCode",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    itn: z.string().meta({ privacy: privacy.id.government }).nullish(),
    taxIdentifierIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish(),
    /** Customer-provided tax IDs from Shopify localization extensions (e.g. Mexico RFC). Stored inline, not in the tax-identifiers collection. */
    customerTaxIds: z
      .array(OutboundShipmentTaxIdSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    mergedTo: zExt.objectId().meta({ privacy: privacy.nonPii }).nullish(),
    ratesErrorMessage: z
      .string()
      .meta({ privacy: privacy.systemErrorText })
      .nullish(),
    mergeSuggestionId: z.string().meta({ privacy: privacy.nonPii }).nullish(), // was going to be an objectId, but you cannot use objectIds for atlas search facets :(
    suggestionRejected: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    disableAutoMerge: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    pointerToNextGroup: zExt
      .objectId()
      .meta({ privacy: privacy.nonPii })
      .nullish(), // If voided, we maintain a pointer to the group that was created as a result of the void
    scanVerified: z
      .object({
        verifiedBy: OMSUserSchema.nullish().meta({
          privacy: privacy.structured,
        }),
        verifiedAt: DateFromDateOrIsoStringSchema.meta({
          privacy: privacy.nonPii,
        }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    scanVerifiedItemized: z
      .record(
        z.string().meta({ privacy: privacy.nonPii }),
        z
          .array(
            z
              .object({
                verifiedBy: z.string().meta({ privacy: privacy.nonPii }),
                quantityVerified: z.number().meta({ privacy: privacy.nonPii }),
                inventoryItemIds: z
                  .array(z.string().meta({ privacy: privacy.nonPii }))
                  .nullish(),
                serialNumbers: z
                  .array(z.string().meta({ privacy: privacy.nonPii }))
                  .nullish(),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
      )
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    packingSlipTemplateId: zExt
      .objectId()
      .meta({ privacy: privacy.nonPii })
      .nullish(), // Custom packing slip template ID for this fulfillment group
    personalizedSlipTemplate: z
      .object({
        templateId: zExt.objectId().meta({ privacy: privacy.nonPii }).nullish(),
        generatedUrl: z
          .string()
          .meta({ privacy: privacy.privateUrl })
          .nullish(), // URL of the generated personalized insert PDF
        // Hash of the template content this PDF was rendered from, so a later
        // template edit can be detected as stale. Absent on pre-hash slips.
        templateHash: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    // Set when a manual personalized-insert selection kicks off async slip
    // generation; cleared by the generatePersonalizedSlipForGroup workflow.
    personalizedSlipGeneratingSince: z
      .date()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    /** Product recommendations generated by the recommendations engine for personalized inserts */
    productRecommendations: z
      .object({
        productIds: z.array(z.string().meta({ privacy: privacy.nonPii })),
        source: createEnum(ProductRecommendationsSource, {
          enumName: "ProductRecommendationsSource",
        })
          .nullish()
          .meta({ privacy: privacy.nonPii }),
        generatedAt: DateFromDateOrIsoStringSchema.meta({
          privacy: privacy.nonPii,
        }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    holds: z
      .array(FulfillmentHoldSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    releaseHoldsAt: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    holdsPlacedByInfo: HoldsPlacedByInfoSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    duplicateOf: zExt.objectId().meta({ privacy: privacy.nonPii }).nullish(),
    autoMergeRanAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    autoMergeCooldown: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    autoMergeLockedAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    events: zExt
      .filterToValidArray(OMSEventSchema)
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    marketplaceNotification: MarketplaceNotificationStatusSchema.nullish().meta(
      { privacy: privacy.structured },
    ),
    ddp: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    // We expect at most one landed cost per fulfillment group today, but use an
    // array to accommodate potential future multi-provider or multi-parcel scenarios.
    landedCosts: z
      .array(LockedLandedCostSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    fulfilledBy: createEnum(FulfilledBy, { enumName: "FulfilledBy" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    // @deprecated Stored at the parcel level instead
    hazmat: HazmatSchema.nullish().meta({ privacy: privacy.nonPii }),
    rateLearningOverwritten: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    parcelLearningOverwritten: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    weightLearningOverwritten: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    overrideReturnAddress: FulfillmentOrderAddressSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    saturdayDelivery: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    assignmentAutomationRanAt: z
      .date()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    archivedInterallyAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    recordedShipment: RecordedShipmentSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    integrationReferences: z
      .array(IntegrationReferenceSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    customerAccountsToGetRatesFor: z
      .array(z.string().meta({ privacy: privacy.untypedPayload }))
      .nullish()
      .meta({ privacy: privacy.untypedPayload }), // unused now, only present for backwards compatibility
    manualFulfillmentInfo: FulfillmentInfoSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    /** TODO: Remove this when IMS is source of truth for comment sold RMR inventory adjustment tracking */
    csInventoryIncrementedLineItemIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish(),
    /** @deprecated Use shippingClaimIdsByTrackingNumber instead */
    shippingClaimIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish(),
    shippingClaimIdsByTrackingNumber: z
      .record(
        z.string().meta({ privacy: privacy.nonPii }),
        z.array(z.string().meta({ privacy: privacy.nonPii })),
      )
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    attemptedProactiveClaim: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    needsRatePreparation: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    routingApplied: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    // @deprecated Stored at the parcel level instead
    dryIceWeight: WeightSchema.nullish().meta({ privacy: privacy.structured }),
    // Effective marketplace completion settings used after label purchase.
    notifyMarketplace: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    notifyCustomer: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    fulfillmentDate: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    marketplaceFulfillmentDelayCriteria:
      MarketplaceFulfillmentDelayCriteriaSchema.nullish(),
    buyReturnLabel: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    sendLabelPurchasedNotification: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    labelPurchasedNotificationSent: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    /** @deprecated Use overriddenAsReceivedFulfillmentOrderIds instead */
    overrideAsReceived: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    overriddenAsReceivedFulfillmentOrderIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish(),
    pickSessionId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    pickStartedOn: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    assignedPickerId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    pickStatus: createEnum(FulfillmentGroupPickStatus, {
      enumName: "FulfillmentGroupPickStatus",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    pickedAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    problemResolvedAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    // When an order is "problem", and this is not present, we assume all items are unpicked
    pickSessionItemStatuses: z
      .array(PickSessionItemStatusSchema)
      .nullish()
      .meta({ privacy: privacy.structured }),
    rerunningAutomationsAt: z
      .date()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    failedShipments: z
      .array(ShipmentPackageSchema)
      .nullish()
      .meta({ privacy: privacy.structured }),
    returnShipment: ShipmentPackageSchema_.extend({
      // used for queries in the cron job - tracking events are the source of truth
      // about scan and balance transactions are the source of truth about charge
      status: createEnum(ReturnShipmentStatus, {
        enumName: "ReturnShipmentStatus",
      })
        .nullish()
        .meta({ privacy: privacy.nonPii }),
    })
      .nullish()
      .meta({ privacy: privacy.structured }),
    isSplit: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    shipmentPaymentOption: ShipmentPaymentOptionSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    // We will eventually move towards shipmentPaymentOptions being the source of truth for payment options - but right now we just use the single option above
    // We are writing to both so that the migration will be trivial
    shipmentPaymentOptions: z
      .array(ShipmentPaymentOptionSchema)
      .nullish()
      .meta({ privacy: privacy.structured }),
    blockedRateProviders: z
      .array(
        createEnum(RateProvider, { enumName: "RateProvider" }).meta({
          privacy: privacy.nonPii,
        }),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
    // Even if autoCleanAddress setting is true, if this is true, we will use the original destination address instead of the verified destination address
    overrideUseOriginalDestinationAddress: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    addressUpdatesMetadata: z
      .object({
        addressFixedBy: createEnum(AddressFixedBy, {
          enumName: "AddressFixedBy",
        })
          .nullish()
          .meta({ privacy: privacy.nonPii }),
        addressFixedAt: DateFromDateOrIsoStringSchema.meta({
          privacy: privacy.nonPii,
        }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    trackingSyncedAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    // A unique index exists on this field (excluding null/undefined) so that we can ensure that we have idempotency when performing operations that create new fulfillment groups in temporal (like moving/voiding fulfillment groups)
    uniqueIdentifier: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    returnShipmentOverrides: z
      .object({
        shopperAddress: FulfillmentOrderAddressSchema.nullish().meta({
          privacy: privacy.structured,
        }),
        merchantAddress: FulfillmentOrderAddressSchema.nullish().meta({
          privacy: privacy.structured,
        }),
        parcel: OutboundParcelSchema.nullish().meta({
          privacy: privacy.structured,
        }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    buyLabelErrors: z
      .array(LabelErrorSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    buyReturnLabelErrors: z
      .array(LabelErrorSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    labelPurchaseFailedAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    forceResyncAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    forceResyncEvenWhenClosedIsh: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    forceResyncInventoryAt: z
      .date()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    packingParcels: PackingParcelsSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    itemParcelAssignments: ItemParcelAssignmentsSchema.nullish().meta({
      privacy: privacy.untypedPayload,
    }),
    parcelsFinalized: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    papsNumber: z.string().meta({ privacy: privacy.id.tracking }).nullish(),
    portOfEntry: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    crossingDate: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    // When a fulfillment group is cancelled in Redo without restocking inventory,
    // this flag is set so IMS can deduct the items from on-hand quantity.
    cancelledWithoutRestock: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    /**
     * Tracks IDs used to ensure idempotent-like behavior across different runs
     * of OMS automation flows.
     */
    idempotencyControl: z
      .object({
        lastSetParcelActionFulfillmentGroupAutomationsRunId: z
          .string()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .transform(({ selectedParcels, ...rest }) => {
    const selectedParcelsWithOverrideWeight =
      selectedParcels ??
      (rest.selectedParcel
        ? [rest.selectedParcel]
        : [
            {
              ...DEFAULT_REDO_PARCEL,
              parcelAndContentWeight: null,
              insurance: null,
              customs: null,
              dryIceWeight: null,
              hazmat: null,
            },
          ]);

    // Back-compat read: legacy documents stored customs only on
    // selectedParcels[0].customs. Back-fill the top-level so all readers see a
    // consistent shape regardless of when the document was written.
    const parcel0Customs = selectedParcelsWithOverrideWeight[0]?.customs;
    if (parcel0Customs) {
      if (
        !rest.customs?.overwritten &&
        !rest.customs?.declarations?.length &&
        parcel0Customs.declarations?.length
      ) {
        rest.customs = {
          declarations: parcel0Customs.declarations,
          overwritten: parcel0Customs.overwritten ?? false,
        };
      }
      rest.contentsType ??= parcel0Customs.contentsType ?? undefined;
      rest.contentsExplanation ??=
        parcel0Customs.contentsExplanation ?? undefined;
      rest.additionalCustomsComments ??=
        parcel0Customs.additionalCustomsComments ?? undefined;
      rest.nonDeliveryOption ??= parcel0Customs.nonDeliveryOption ?? undefined;
      rest.uspsExemptionCode ??= parcel0Customs.uspsExemptionCode ?? undefined;
      rest.itn ??= parcel0Customs.itn ?? undefined;
      rest.taxIdentifierIds ??= parcel0Customs.taxIdentifierIds ?? undefined;
    }

    if (selectedParcelsWithOverrideWeight[0]) {
      selectedParcelsWithOverrideWeight[0] = {
        ...selectedParcelsWithOverrideWeight[0],
        parcelAndContentWeight:
          selectedParcelsWithOverrideWeight[0]?.parcelAndContentWeight ??
          rest.overrideWeight,
        customs: {
          itn: rest.itn,
          uspsExemptionCode: rest.uspsExemptionCode,
          nonDeliveryOption: rest.nonDeliveryOption,
          contentsType: rest.contentsType,
          contentsExplanation: rest.contentsExplanation,
          additionalCustomsComments: rest.additionalCustomsComments,
          taxIdentifierIds: rest.taxIdentifierIds,
          declarations: rest.customs?.declarations,
          ...selectedParcelsWithOverrideWeight[0]?.customs,
        },
        insurance: {
          includeInsurance: rest.includeInsurance,
          insuredAmount: rest.insuredAmount,
          ...selectedParcelsWithOverrideWeight[0]?.insurance,
        },
        hazmat: selectedParcelsWithOverrideWeight[0]?.hazmat,
        dryIceWeight: selectedParcelsWithOverrideWeight[0]?.dryIceWeight,
      };
    }

    if (rest.shipment) {
      rest.shipment.recordedShipment ??= rest.recordedShipment;
    }

    if (rest.shipmentRates?.rates?.length) {
      rest.shipmentRates.rates = rest.shipmentRates.rates.map(
        ensureCarrierAccountId,
      );
    }

    return { ...rest, selectedParcels: selectedParcelsWithOverrideWeight };
  });

export const FulfillmentGroupSchema: z.ZodType<FulfillmentGroup> =
  fulfillmentGroupSchema_;
export interface FulfillmentGroup extends z.infer<
  typeof fulfillmentGroupSchema_
> {}

export const FulfillmentGroupWithoutFulfillmentsSchema =
  FulfillmentGroupSchema.transform(
    ({ fulfillments: _fulfillments, ...group }) => group,
  );

const fulfillmentGroupBinMonitoringSchema = z.object({
  _id: zExt.objectId(),
  integrationReferences: z.array(IntegrationReferenceSchema).nullish(),
  fulfillmentOrders: z.array(FulfillmentOrderSchema_.pick({ lineItems: true })),
  summary: FulfillmentGroupSummarySchema_.pick({
    providerCreatedAt: true,
    lineItemLocations: true,
  }),
});
export const FulfillmentGroupBinMonitoringSchema: z.ZodType<FulfillmentGroupBinMonitoring> =
  fulfillmentGroupBinMonitoringSchema;
export type FulfillmentGroupBinMonitoring = z.infer<
  typeof fulfillmentGroupBinMonitoringSchema
>;

const fulfillmentGroupResyncStatusSchema = z.object({
  status: createEnum(FulfillmentGroupStatus, {
    enumName: "FulfillmentGroupStatus",
  }),
  fulfillmentOrders: z.array(
    FulfillmentOrderSchema_.pick({ status: true }).extend({
      order: FulfillmentOrderOrderSchema_.pick({
        displayFinancialStatus: true,
      }).nullish(),
    }),
  ),
});
export const FulfillmentGroupResyncStatusSchema: z.ZodType<FulfillmentGroupResyncStatus> =
  fulfillmentGroupResyncStatusSchema;
export type FulfillmentGroupResyncStatus = z.infer<
  typeof fulfillmentGroupResyncStatusSchema
>;

const fulfillmentServiceSchema_ = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  name: z.string().meta({ privacy: privacy.nonPii }),
});
export const fulfillmentServiceSchema: z.ZodType<FulfillmentService> =
  fulfillmentServiceSchema_;
export interface FulfillmentService extends z.infer<
  typeof fulfillmentServiceSchema_
> {}

export const ProviderActionMessagesSchema = z.object({
  errorMessage: z
    .string()
    .meta({ privacy: privacy.systemErrorText })
    .nullable(),
  warningMessage: z
    .string()
    .meta({ privacy: privacy.text.thirdParty })
    .nullable(),
});
export type ProviderActionMessages = z.infer<
  typeof ProviderActionMessagesSchema
>;

const fulfillmentGroupDataSchema_ = FulfillmentGroupSummarySchema_.extend({
  hasReceivedOverride: z.boolean().meta({ privacy: privacy.nonPii }).optional(),
  _id: z.string().meta({ privacy: privacy.nonPii }),
  tags: z.array(TagSchema).nullish().meta({ privacy: privacy.structured }),
  releaseHoldsAt: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  orders: z.array(
    z.object({
      redoOrderId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      name: z.string().meta({ privacy: privacy.nonPii }),
      secondaryOrderName: z
        .string()
        .meta({ privacy: privacy.nonPii })
        .nullish(),
      tags: z
        .array(z.string().meta({ privacy: privacy.untypedPayload }))
        .meta({ privacy: privacy.untypedPayload }),
      id: z.string().meta({ privacy: privacy.nonPii }),
      packageProtected: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
      fulfillmentOrderExternalId: z.string().meta({ privacy: privacy.nonPii }),
      customAttributes: z
        .array(
          z.object({
            key: z.string().meta({ privacy: privacy.nonPii }),
            value: z
              .string()
              .meta({ privacy: privacy.text.customer })
              .nullish(),
          }),
        )
        .nullish(),
    }),
  ),
  /**
   * @deprecated Use fulfillmentOrderToDeliveryMethod instead
   */
  shippingMethodNames: z.array(z.string().meta({ privacy: privacy.nonPii })),
  fulfillmentOrderToDeliveryMethod: z.record(
    z.string().meta({ privacy: privacy.nonPii }),
    z.object({
      name: z.string().meta({ privacy: privacy.nonPii }),
      code: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      kind: createEnum(RedoShopifyDeliveryMethodType, {
        enumName: "RedoShopifyDeliveryMethodType",
      }).meta({ privacy: privacy.nonPii }),
    }),
  ),
  batchId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  prettyBatchId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  /**
   * WARNING: Line items may contain sub items from Bill of Materials (BOM) expansion.
   * If the use case is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use `expandLineItemsToPickableItems(items)` to get only the leaf node items.
   * In most cases (splitting, table filtering, etc) - you probably want to use the top level line items - the actual line items that were ordered.
   */
  items: z.array(
    FulfillmentOrderLineItemSchema_.extend({
      fulfillmentOrderExternalId: z.string().meta({ privacy: privacy.nonPii }),
      lots: z
        .array(LineItemLotSchema)
        .optional()
        .meta({ privacy: privacy.structured }),
    }),
  ),
  insuranceProvider: createEnum(OmsInsuranceProvider, {
    enumName: "OmsInsuranceProvider",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  protectedByPackageProtectionPlus: z
    .boolean()
    .meta({ privacy: privacy.nonPii }),
  originAddress: FulfillmentOrderAddressSchema.meta({
    privacy: privacy.structured,
  }),
  verifiedOriginAddress: VerifiedAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  destinationAddress: FulfillmentOrderAddressSchema.meta({
    privacy: privacy.structured,
  }),
  verifiedDestinationAddress: VerifiedAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  billingAddress: FulfillmentOrderAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  status: createEnum(FulfillmentGroupStatus, {
    enumName: "FulfillmentGroupStatus",
  }).meta({ privacy: privacy.nonPii }),
  rerunningAutomationsAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  selectedParcels: z
    .array(OutboundParcelSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  customerShippingPaid: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  availableShipmentRates: ShipmentRatesSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  selectedShippingRate: GroupSelectedRateSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  displayWeightGrams: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  assignedUser: (OMSUserSchema as IOMSUser)
    .nullish()
    .meta({ privacy: privacy.structured }),
  /** @deprecated Use shipmentSummary; sent only for backwards compatibility. */
  shipmentDetails: ShipmentDetailsSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  /** @deprecated Use shipmentSummary; sent only for backwards compatibility. */
  shipment: ShipmentPackageSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  /** @deprecated Use shipmentSummary; sent only for backwards compatibility. */
  additionalShipments: z
    .array(ShipmentPackageSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  shipmentSummary: ShipmentSummarySchema.nullish().meta({
    privacy: privacy.structured,
  }),
  /** Present iff the group was marked as fulfilled by hand in Redo. */
  manualFulfillmentInfo: FulfillmentInfoSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  returnLabelUrl: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  returnTrackingStatus: z
    .array(
      createEnum(ShipmentTrackerStatus, {
        enumName: "ShipmentTrackerStatus",
      }).meta({ privacy: privacy.nonPii }),
    )
    .nullish(),
  returnTrackingCodes: z
    .array(
      z.object({
        code: z.string().meta({ privacy: privacy.nonPii }),
        link: z.string().meta({ privacy: privacy.nonPii }),
      }),
    )
    .nullish(),
  noteToBuyer: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
  noteFromBuyer: z.string().meta({ privacy: privacy.text.customer }).nullish(),
  internalNote: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
  marketplaceInternalNote: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  customAttributes: z.array(
    z.object({
      key: z.string().meta({ privacy: privacy.nonPii }),
      value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    }),
  ),
  customerName: z
    .string()
    .meta({ privacy: privacy.person_name.customer })
    .nullish(),
  customerEmail: z.string().meta({ privacy: privacy.email.customer }).nullish(),
  customerPhone: z.string().meta({ privacy: privacy.phone.customer }).nullish(),
  fulfillBy: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  printStatus: PrintStatusSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  orderSources: z
    .array(createEnum(Provider, { enumName: "Provider" }))
    .meta({ privacy: privacy.nonPii }),
  risk: (RiskSchema as IRisk).nullish().meta({ privacy: privacy.structured }),
  ratesPendingSince: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  signatureRequired: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  adultSignatureRequired: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  /** @deprecated Stored at the parcel level instead */
  includeInsurance: z.boolean().meta({ privacy: privacy.nonPii }).nullish(), // if this is true but insuredAmount is not set, we insure the whole package
  /** @deprecated Stored at the parcel level instead */
  insuredAmount: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  specialRateEligibility: createEnum(SpecialRateEligibility, {
    enumName: "SpecialRateEligibility",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  ratesErrorMessage: z
    .string()
    .meta({ privacy: privacy.systemErrorText })
    .nullish(),
  mergedTo: zExt.objectId().meta({ privacy: privacy.nonPii }).nullish(),
  pointerToNextGroup: zExt
    .objectId()
    .meta({ privacy: privacy.nonPii })
    .nullish(), // If voided, we maintain a pointer to the group that was created as a result of the void
  /** @deprecated Not used anymore */
  insured: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  fulfilledBy: createEnum(FulfilledBy, { enumName: "FulfilledBy" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  // Stores is really sales channels - we will remove this once everything is deployed
  stores: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .meta({ privacy: privacy.nonPii }),
  salesChannels: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish(),
  originStore: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  scanVerified: z
    .object({
      verifiedBy: (OMSUserSchema as IOMSUser)
        .nullish()
        .meta({ privacy: privacy.structured }),
      verifiedAt: DateFromDateOrIsoStringSchema.meta({
        privacy: privacy.nonPii,
      }).nullish(),
    })
    .nullish(),
  packingSlipTemplateId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  fulfillmentOrderIds: z.array(z.string().meta({ privacy: privacy.nonPii })),
  events: zExt
    .filterToValidArray(OMSEventSchema)
    .nullish()
    .meta({ privacy: privacy.untypedPayload }),
  customs: OutboundParcelCustomsSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  marketplaceNotification: (
    MarketplaceNotificationStatusSchema as IMarketplaceNotificationStatus
  ).nullish(),
  notifyMarketplace: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  fulfillmentDate: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  marketplaceFulfillmentDelayCriteria:
    MarketplaceFulfillmentDelayCriteriaSchema.nullish(),
  locationsForMove: z
    .array(LocationForMoveSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  selectedLocationId: z.string().meta({ privacy: privacy.nonPii }).nullable(),
  contentsType: createEnum(ContentsType, { enumName: "ContentsType" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  contentsExplanation: z
    .string()
    .meta({ privacy: privacy.text.merchant })
    .nullish(),
  nonDeliveryOption: createEnum(NonDeliveryOption, {
    enumName: "NonDeliveryOption",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  uspsExemptionCode: createEnum(USPSExemptionCode, {
    enumName: "USPSExemptionCode",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  itn: z.string().meta({ privacy: privacy.id.government }).nullish(),
  taxIdentifierIds: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish(),
  customerTaxIds: z
    .array(OutboundShipmentTaxIdSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  ddp: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  duplicateOf: zExt.objectId().meta({ privacy: privacy.nonPii }).nullish(),
  hazmat: HazmatSchema.nullish().meta({ privacy: privacy.nonPii }),
  providers: z
    .array(createEnum(Provider, { enumName: "Provider" }))
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  usedLearning: z
    .record(
      createEnum(LearnedPresetKind, { enumName: "LearnedPresetKind" }).meta({
        privacy: privacy.nonPii,
      }),
      z.boolean().meta({ privacy: privacy.nonPii }),
    )
    .nullish()
    .meta({ privacy: privacy.structured }),
  overrideReturnAddress: FulfillmentOrderAddressSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  addressManuallyUpdated: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .default(false),
  saturdayDelivery: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  canPlaceHold: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  assignedFulfillmentService: fulfillmentServiceSchema
    .nullish()
    .meta({ privacy: privacy.structured }),
  teamInfo: z
    .object({
      _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
      name: z.string().meta({ privacy: privacy.nonPii }),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
  /** @deprecated Use shippingClaimIdsByTrackingNumber instead */
  shippingClaimIds: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish(),
  shippingClaimIdsByTrackingNumber: z
    .record(
      z.string().meta({ privacy: privacy.nonPii }),
      z.array(z.string().meta({ privacy: privacy.nonPii })),
    )
    .nullish()
    .meta({ privacy: privacy.untypedPayload }),
  attemptedProactiveClaim: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  dryIceWeight: WeightSchema.nullish().meta({ privacy: privacy.structured }),
  providerActionMessages: ProviderActionMessagesSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  supportedActions: z.array(
    createEnum(RedoShopifyFulfillmentOrderAction, {
      enumName: "RedoShopifyFulfillmentOrderAction",
    }).meta({ privacy: privacy.nonPii }),
  ),
  buyReturnLabel: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  sendLabelPurchasedNotification: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  labelPurchasedNotificationSent: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  isRefunded: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  needsRatePreparation: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  routingApplied: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  shipmentPaymentOption: (
    ShipmentPaymentOptionSchema as IShipmentPaymentOption
  ).nullish(),
  customDate1: z
    .array(DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }))
    .nullish(),
  customDate2: z
    .array(DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }))
    .nullish(),
  customDate3: z
    .array(DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }))
    .nullish(),
  customString1: z
    .array(z.string().meta({ privacy: privacy.text.merchant }))
    .nullish(),
  customString2: z
    .array(z.string().meta({ privacy: privacy.text.merchant }))
    .nullish(),
  customString3: z
    .array(z.string().meta({ privacy: privacy.text.merchant }))
    .nullish(),
  customNumber1: z
    .array(z.number().meta({ privacy: privacy.nonPii }))
    .nullish(),
  customNumber2: z
    .array(z.number().meta({ privacy: privacy.nonPii }))
    .nullish(),
  customNumber3: z
    .array(z.number().meta({ privacy: privacy.nonPii }))
    .nullish(),
  customAttributeDate1: z
    .array(DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }))
    .nullish(),
  customAttributeDate2: z
    .array(DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }))
    .nullish(),
  customAttributeDate3: z
    .array(DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }))
    .nullish(),
  customAttributeString1: z
    .array(z.string().meta({ privacy: privacy.untypedPayload }))
    .nullish(),
  customAttributeString2: z
    .array(z.string().meta({ privacy: privacy.untypedPayload }))
    .nullish(),
  customAttributeString3: z
    .array(z.string().meta({ privacy: privacy.untypedPayload }))
    .nullish(),
  customAttributeNumber1: z
    .array(z.number().meta({ privacy: privacy.nonPii }))
    .nullish(),
  customAttributeNumber2: z
    .array(z.number().meta({ privacy: privacy.nonPii }))
    .nullish(),
  customAttributeNumber3: z
    .array(z.number().meta({ privacy: privacy.nonPii }))
    .nullish(),
  uniqueProductsCount: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  slaFulfillmentDate: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  carbonNeutral: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  canadaPostOption: createEnum(CanadaPostOption, {
    enumName: "CanadaPostOption",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  dhlEcommerceLabelOptions: DhlEcommerceLabelOptionsSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  directDeliveryOnly: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  overrideResidentialAddress: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  additionalCustomsComments: z
    .string()
    .meta({ privacy: privacy.text.merchant })
    .nullish(),
  personalizedSlipTemplateId: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  personalizedSlipGeneratingSince: z
    .date()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  // URL of the most recently generated insert PDF, surfaced for the slideout's
  // "View insert" action. Present only once a slip has been generated.
  personalizedSlipGeneratedUrl: z
    .string()
    .meta({ privacy: privacy.privateUrl })
    .nullish(),
  overrideUseOriginalDestinationAddress: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  shipmentMessages: z
    .record(
      z.string().meta({ privacy: privacy.nonPii }),
      z.array(ShipmentMessageSchema),
    )
    .nullish()
    .meta({ privacy: privacy.untypedPayload }),
  paymentGateways: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish(),
  buyLabelErrors: z
    .array(LabelErrorSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  buyReturnLabelErrors: z
    .array(LabelErrorSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  pickSessionId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  pickStartedOn: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  assignedPicker: (OMSUserSchema as IOMSUser)
    .nullish()
    .meta({ privacy: privacy.structured }),
  pickStatus: createEnum(FulfillmentGroupPickStatus, {
    enumName: "FulfillmentGroupPickStatus",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  pickedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  problemResolvedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullish(),
  // When an order is "problem", and this is not present, we assume all items are unpicked
  pickSessionItemStatuses: z
    .array(PickSessionItemStatusSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  assignedTotes: z
    .array(RedoFulfillmentToteSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  // Id of the purchase order linked to this fulfillment group, if any.
  // Enriched at read time from the inventory store (purchase_order.fulfillment_group_id).
  linkedPurchaseOrderId: purchaseOrderIdFormat.schema
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  selectedShippingRateSetByInfo:
    SelectedShippingRateSetByInfoSchema.nullish().meta({
      privacy: privacy.structured,
    }),
  selectedShippingRateSetBy: createEnum(SelectedShippingRateSetBy, {
    enumName: "SelectedShippingRateSetBy",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  holdsPlacedByInfo: HoldsPlacedByInfoSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  holds: z
    .array(FulfillmentHoldSchema)
    .nullish()
    .meta({ privacy: privacy.structured }),
  scanVerifiedItemized: z
    .record(
      z.string().meta({ privacy: privacy.nonPii }),
      z.array(
        z.object({
          verifiedBy: z.string().meta({ privacy: privacy.nonPii }),
          quantityVerified: z.number().meta({ privacy: privacy.nonPii }),
          inventoryItemIds: z
            .array(z.string().meta({ privacy: privacy.nonPii }))
            .nullish(),
          serialNumbers: z
            .array(z.string().meta({ privacy: privacy.nonPii }))
            .nullish(),
        }),
      ),
    )
    .nullish()
    .meta({ privacy: privacy.untypedPayload }),
  requiresSerialNumberByLineItemExternalId: z
    .record(
      z.string().meta({ privacy: privacy.nonPii }),
      z.boolean().meta({ privacy: privacy.nonPii }),
    )
    .nullish(),
  packingParcels: PackingParcelsSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  itemParcelAssignments: ItemParcelAssignmentsSchema.nullish().meta({
    privacy: privacy.untypedPayload,
  }),
  parcelsFinalized: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  hasLandedCost: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  // Landed cost (duties, taxes, fees) paid at checkout, shop-currency major
  // units; present only when the selected checkout option carried landed cost.
  landedCost: z
    .object({
      duties: z.number().meta({ privacy: privacy.nonPii }),
      taxes: z.number().meta({ privacy: privacy.nonPii }),
      fees: z.number().meta({ privacy: privacy.nonPii }),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
  lineItemPickability: z
    .array(
      z.object({
        id: z.string().meta({ privacy: privacy.nonPii }),
        externalId: z.string().meta({ privacy: privacy.nonPii }).optional(),
        title: z.string().meta({ privacy: privacy.nonPii }),
        productTitle: z.string().meta({ privacy: privacy.nonPii }),
        variantTitle: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        sku: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        barcode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        quantity: z.number().meta({ privacy: privacy.nonPii }),
        quantityAvailable: z.number().meta({ privacy: privacy.nonPii }),
        quantityCommitted: z
          .number()
          .meta({ privacy: privacy.nonPii })
          .optional(),
        quantityPicked: z.number().meta({ privacy: privacy.nonPii }).optional(),
        isPickable: z.boolean().meta({ privacy: privacy.nonPii }),
        isUnpicked: z.boolean().meta({ privacy: privacy.nonPii }).optional(),
        resolvable: z.boolean().meta({ privacy: privacy.nonPii }).optional(),
        isPickableIncremental: z
          .boolean()
          .meta({ privacy: privacy.nonPii })
          .optional(),
        demand: z.number().meta({ privacy: privacy.nonPii }).optional(),
        isNonPicked: z.boolean().meta({ privacy: privacy.nonPii }).optional(),
        hasNoProduct: z.boolean().meta({ privacy: privacy.nonPii }).optional(),
        hasUncountedBin: z
          .boolean()
          .meta({ privacy: privacy.nonPii })
          .optional(),
        hasImsInventoryItemId: z
          .boolean()
          .meta({ privacy: privacy.nonPii })
          .optional(),
        pickableBinCount: z
          .number()
          .meta({ privacy: privacy.nonPii })
          .optional(),
        earliestBinAssignmentAt: z.coerce
          .date()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
        workOrderStatus: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        workOrderStep: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        // Per-ordinal WO state (FGLI index v24+). Absent on pre-v24 docs.
        quantityPending: z
          .number()
          .meta({ privacy: privacy.nonPii })
          .optional(),
        quantityReady: z.number().meta({ privacy: privacy.nonPii }).optional(),
        workOrders: z
          .array(
            z.object({
              ordinal: z.number().meta({ privacy: privacy.nonPii }),
              status: z.string().meta({ privacy: privacy.nonPii }),
              step: z.string().meta({ privacy: privacy.nonPii }).nullish(),
            }),
          )
          .optional(),
        // True when this item is pickable without inventory under the team's
        // allowed-problem-pick policy (eligibility instant has arrived).
        allowedByPolicy: z
          .boolean()
          .meta({ privacy: privacy.nonPii })
          .optional(),
        pickableUntil: z.coerce
          .date()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
        imageUrl: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        bins: z
          .array(
            z.object({
              storageLocationId: z.string().meta({ privacy: privacy.nonPii }),
              storageLocationName: z.string().meta({ privacy: privacy.nonPii }),
              storageLocationType: z
                .string()
                .meta({ privacy: privacy.nonPii })
                .optional(),
              priority: z.string().meta({ privacy: privacy.nonPii }).nullish(),
              quantity: z.number().meta({ privacy: privacy.nonPii }),
              lastCountedAt: z.coerce
                .date()
                .meta({ privacy: privacy.nonPii })
                .nullish(),
              hasPendingReplenishment: z
                .boolean()
                .meta({ privacy: privacy.nonPii })
                .optional(),
              inventoryLevelCreatedAt: z.coerce
                .date()
                .meta({ privacy: privacy.nonPii })
                .nullish(),
              lotNumber: z.string().meta({ privacy: privacy.nonPii }).nullish(),
              expirationDate: z.coerce
                .date()
                .meta({ privacy: privacy.nonPii })
                .nullish(),
              inventoryItemClass: z
                .string()
                .meta({ privacy: privacy.nonPii })
                .nullish(),
            }),
          )
          .nullish(),
      }),
    )
    .nullish(),
  pickabilityStatus: z
    .enum([
      "all_pickable",
      "partially_pickable",
      "not_pickable",
      "all_pending_wo",
      "partially_pending_wo",
      "picking",
    ])
    .nullish(),
});
export const FulfillmentGroupDataSchema: z.ZodType<FulfillmentGroupData> =
  fulfillmentGroupDataSchema_;
export type FulfillmentGroupData = {} & z.infer<
  typeof fulfillmentGroupDataSchema_
>;

const FulfillmentGroupDataAsyncInfoSchema_ = z.object({
  locations: z.array(
    RedoFulfillmentLocationSchema.meta({ privacy: privacy.structured }),
  ),
  fulfillmentServices: z.array(
    fulfillmentServiceSchema.meta({ privacy: privacy.structured }),
  ),
  degroupByCustomAttributes: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  packageProtectionPlusEnabled: z.boolean().meta({ privacy: privacy.nonPii }),
  claimPolicyContext: z
    .object({
      autoClaimsEnabled: z.boolean().meta({ privacy: privacy.nonPii }),
      whitelistedCarrierAccountIds: z.array(
        z.string().meta({ privacy: privacy.nonPii }),
      ),
      blacklistedCarrierAccountIds: z.array(
        z.string().meta({ privacy: privacy.nonPii }),
      ),
      loaSignedCarrierAccountIds: z.array(
        z.string().meta({ privacy: privacy.nonPii }),
      ),
    })
    .optional(),
  showBomInputsInGroupTable: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  teamUsers: z.array(OMSUserSchema.meta({ privacy: privacy.structured })),
  teamInfo: fulfillmentServiceSchema
    .nullable()
    .meta({ privacy: privacy.structured }),
});
export const FulfillmentGroupDataAsyncInfoSchema: z.ZodType<FulfillmentGroupDataAsyncInfo> =
  FulfillmentGroupDataAsyncInfoSchema_;
export interface FulfillmentGroupDataAsyncInfo extends z.infer<
  typeof FulfillmentGroupDataAsyncInfoSchema_
> {}

export function sortFulfillmentOrderLineItems<
  T extends FulfillmentOrderLineItem,
>(orderBy: ItemOrderBy, items: T[]): T[] {
  switch (orderBy) {
    case ItemOrderBy.QuantityDesc:
      return items.sort((a, b) => b.quantity - a.quantity);
    case ItemOrderBy.QuantityAsc:
      return items.sort((a, b) => a.quantity - b.quantity);
    case ItemOrderBy.NameAsc:
      return items.sort((a, b) => a.title.trim().localeCompare(b.title.trim()));
    case ItemOrderBy.NameDesc:
      return items.sort((a, b) => b.title.trim().localeCompare(a.title.trim()));
    case ItemOrderBy.SkuAsc:
      return items.sort((a, b) =>
        (a.sku || "").trim().localeCompare((b.sku || "").trim()),
      );
    case ItemOrderBy.SkuDesc:
      return items.sort((a, b) =>
        (b.sku || "").trim().localeCompare((a.sku || "").trim()),
      );
    case ItemOrderBy.TotalPriceAsc:
      return items.sort(
        (a, b) => Number(a.totalPrice.amount) - Number(b.totalPrice.amount),
      );
    case ItemOrderBy.TotalPriceDesc:
      return items.sort(
        (a, b) => Number(b.totalPrice.amount) - Number(a.totalPrice.amount),
      );
    case ItemOrderBy.UnitPriceAsc:
      return items.sort(
        (a, b) => Number(a.unitPrice.amount) - Number(b.unitPrice.amount),
      );
    case ItemOrderBy.UnitPriceDesc:
      return items.sort(
        (a, b) => Number(b.unitPrice.amount) - Number(a.unitPrice.amount),
      );
    case ItemOrderBy.BinAsc:
      return items.sort((a, b) =>
        (a.bin || "")
          .trim()
          .localeCompare((b.bin || "").trim(), undefined, { numeric: true }),
      );
    case ItemOrderBy.BinDesc:
      return items.sort((a, b) =>
        (b.bin || "")
          .trim()
          .localeCompare((a.bin || "").trim(), undefined, { numeric: true }),
      );
    case ItemOrderBy.OrdersAsc:
      return items.sort((a, b) =>
        (a.orderId || "")
          .trim()
          .localeCompare((b.orderId || "").trim(), undefined, {
            numeric: true,
          }),
      );
    case ItemOrderBy.OrdersDesc:
      return items.sort((a, b) =>
        (b.orderId || "")
          .trim()
          .localeCompare((a.orderId || "").trim(), undefined, {
            numeric: true,
          }),
      );
    case ItemOrderBy.VendorAsc:
      return items.sort((a, b) =>
        (a.vendor || "").trim().localeCompare((b.vendor || "").trim()),
      );
    case ItemOrderBy.VendorDesc:
      return items.sort((a, b) =>
        (b.vendor || "").trim().localeCompare((a.vendor || "").trim()),
      );
  }
}

export function getWeightWithParcelsGrams(
  totalWeightGrams: number,
  parcels: Parcel[] | null | undefined,
): Weight {
  const orderWeight: Weight = { unit: WeightUnit.G, value: totalWeightGrams };

  if (!parcels) {
    return orderWeight;
  }

  const parcelWeights = parcels.map((p) => getParcelWeightGrams(p));
  const totalWeight = parcelWeights.reduce((acc, parcelWeight) => {
    return addWeight(acc, parcelWeight);
  }, orderWeight);

  return convertWeight(totalWeight, WeightUnit.G);
}

export function getDisplayWeightGrams(
  overrideWeight: Weight | null | undefined,
  totalWeightWithParcelGrams: number,
): number {
  return overrideWeight
    ? convertWeight(overrideWeight, WeightUnit.G).value
    : totalWeightWithParcelGrams;
}

export function getSelectedRate({
  selectedShippingRate,
  availableRates,
}: {
  selectedShippingRate: SelectedShippingRate | null | undefined;
  availableRates: OutboundRate[] | null | undefined;
}): OutboundRate | undefined {
  if (!selectedShippingRate || !availableRates) {
    return undefined;
  }

  return availableRates.find((rate) => {
    let condition =
      carrierToLabel[rate.carrier] ===
        carrierToLabel[selectedShippingRate.carrier] &&
      servicesMatch(rate.service, selectedShippingRate.service);

    if (selectedShippingRate.carrier_account_id) {
      condition =
        condition &&
        carrierAccountIdMatchesRate(
          selectedShippingRate.carrier_account_id,
          rate,
        );
    }

    return condition;
  });
}

export function getFulfillmentOrdersByOrder(
  groups: FulfillmentGroup[],
): Record<string, FulfillmentOrder[]> {
  const byOrder = groups
    .flatMap((group) => group.fulfillmentOrders)
    .reduce(
      (acc, fulfillmentOrder) => {
        if (fulfillmentOrder.order) {
          const existing = acc[fulfillmentOrder.order.id] ?? [];
          existing.push(fulfillmentOrder);
          acc[fulfillmentOrder.order.id] = existing;
        }
        return acc;
      },
      {} as Record<string, FulfillmentOrder[]>,
    );
  return byOrder;
}

export function getFulfillmentGroupsOrderIds(
  groups: FulfillmentGroup[],
): string[] {
  const allOrders = groups
    .flatMap((group) => group.fulfillmentOrders.map((fo) => fo.order?.id))
    .filter((id): id is string => id !== undefined);
  const uniqueOrders = new Set(allOrders);
  return Array.from(uniqueOrders);
}

/**
 * Gets the total quantity of items to scan/verify.
 * When `hideInputItems` is true, returns the parent (`totalItemQuantity`) count —
 * the merchant has opted to pick/scan BOM parents rather than inputs.
 * Otherwise returns `expandedItemQuantity` (true BOM-expanded count) if available,
 * falling back to `totalItemQuantity`.
 */
export function getScanItemQuantity(
  fulfillmentGroup: FulfillmentGroupData | null | undefined,
  options?: { hideInputItems?: boolean },
): number {
  if (!fulfillmentGroup) {
    return 0;
  }
  if (options?.hideInputItems) {
    return fulfillmentGroup.totalItemQuantity;
  }
  return (
    fulfillmentGroup.expandedItemQuantity ?? fulfillmentGroup.totalItemQuantity
  );
}

/**
 * Checks if the fulfillment group has any BOM-expanded items.
 * Returns true if any line item has billOfMaterialsInfos with expansion items.
 */
export function hasBomExpansion(
  fulfillmentGroup: FulfillmentGroupData,
): boolean {
  return fulfillmentGroup.items.some((item) =>
    item.billOfMaterialsInfos?.some((info) => info.expansionItems.length > 0),
  );
}

export function getFulfillmentGroupValue(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
): number {
  if ("summary" in fulfillmentGroup) {
    // Calculate total including shipping and taxes (not just subtotal)
    const subtotal =
      fulfillmentGroup.summary.totalDiscountedPrice != null
        ? Number(fulfillmentGroup.summary.totalDiscountedPrice.amount)
        : Number(fulfillmentGroup.summary.totalPrice.amount);
    const shippingCost = Number(
      fulfillmentGroup.summary.totalShippingCost.amount,
    );
    const taxes = Number(fulfillmentGroup.summary.totalTax.amount);
    return subtotal + shippingCost + taxes;
  } else {
    const subtotal =
      fulfillmentGroup.totalDiscountedPrice != null
        ? Number(fulfillmentGroup.totalDiscountedPrice.amount)
        : Number(fulfillmentGroup.totalPrice.amount);
    const shippingCost = Number(fulfillmentGroup.totalShippingCost.amount);
    const taxes = Number(fulfillmentGroup.totalTax.amount);
    return subtotal + shippingCost + taxes;
  }
}

export function findShipmentByTracker(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
  trackingNumber: string,
): ShipmentPackage | undefined | null {
  return [
    fulfillmentGroup.shipment,
    ...(fulfillmentGroup.additionalShipments ?? []),
  ].find((s) => s?.tracker?.trackingCode === trackingNumber);
}

export const knownRatesErrorMessages = [
  "No HS codes found for products",
  "The parameters passed contained an invalid character.",
  "Some SKUs may be truncated on the shipping label",
  "Origin address zip is required",
  "Origin address line 1 contains bad characters",
  "Destination address line 1 contains bad characters",
  "Destination address name contains bad characters",
  "Address line 1 may need to be fewer than 30 alphanumeric characters",
  "Contents explanation required - item descriptions will be used as a fallback",
];

export function uniqueOrderNames(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
) {
  if ("fulfillmentOrders" in fulfillmentGroup) {
    const orderNames = fulfillmentGroup.fulfillmentOrders
      .map((fo) => fo.order?.name)
      .filter((name) => name != null);
    return [...new Set(orderNames)];
  }
  return [...new Set(fulfillmentGroup.orders.map((o) => o.name))];
}

export function shouldUseVerifiedDestinationAddress(
  group: FulfillmentGroupData | FulfillmentGroup,
  autoCleanAddresses: boolean | undefined | null,
): boolean {
  if (group.overrideUseOriginalDestinationAddress) {
    return false;
  }
  return !!autoCleanAddresses;
}

export function getMergeHoldId(fulfillmentOrderId: string) {
  return "redo-merge-" + fulfillmentOrderId;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const FulfillmentGroupKeys = Object.keys(
  FulfillmentGroupSchema,
) as (keyof FulfillmentGroup)[];

export function fulfillmentGroupExhaustiveCheck() {
  for (const k of FulfillmentGroupKeys) {
    switch (k) {
      case "_id":
      case "numericId":
      case "uniqueIdentifier":
      case "createdAt":
      case "updatedAt":
      case "team":
      case "teamName":
      case "assignedFulfillmentService":
      case "assignedUserId":
      case "fulfillmentOrders":
      case "summary":
      case "status":
      case "selectedParcels":
      case "selectedShippingRate":
      case "selectedParcel": // deprecated
      case "selectedShippingRateSetBy": // deprecated
      case "selectedShippingRateSetByInfo":
      case "rateSelectionCriteria":
      case "insuranceSelectionCriteria":
      case "shipmentRates":
      case "ratesPendingSince":
      case "ratesErrorMessage":
      case "blockedRateProviders":
      case "overrideWeight": // deprecated
      case "displayWeightGrams":
      case "dryIceWeight":
      case "noteToBuyer":
      case "noteFromBuyer":
      case "internalNote":
      case "tags":
      case "shipment":
      case "additionalShipments":
      case "fulfillments":
      case "failedShipments":
      case "recordedShipment":
      case "printStatus":
      case "packingSlipTemplateId":
      case "personalizedSlipTemplate":
      case "personalizedSlipGeneratingSince":
      case "learnedPresets":
      case "rateLearningOverwritten":
      case "parcelLearningOverwritten":
      case "weightLearningOverwritten":
      case "needsRatePreparation":
      case "routingApplied":
      case "assignmentAutomationRanAt":
      case "rerunningAutomationsAt":
      case "signatureRequired":
      case "carbonNeutral":
      case "dhlEcommerceLabelOptions":
      case "directDeliveryOnly":
      case "adultSignatureRequired":
      case "saturdayDelivery":
      case "hazmat":
      case "ddp":
      case "includeInsurance":
      case "insuredAmount":
      case "specialRateEligibility":
      case "overrideDestinationAddresses":
      case "addressManuallyUpdated":
      case "overrideOriginAddress":
      case "overrideReturnAddress":
      case "overrideResidentialAddress":
      case "overrideUseOriginalDestinationAddress":
      case "overrideDeliveryMethod":
      case "addressUpdatesMetadata":
      case "customs":
      case "contentsType":
      case "contentsExplanation":
      case "additionalCustomsComments":
      case "nonDeliveryOption":
      case "uspsExemptionCode":
      case "itn":
      case "taxIdentifierIds":
      case "customerTaxIds":
      case "mergedTo":
      case "pointerToNextGroup":
      case "duplicateOf":
      case "mergeSuggestionId":
      case "suggestionRejected":
      case "disableAutoMerge":
      case "autoMergeRanAt":
      case "autoMergeCooldown":
      case "autoMergeLockedAt":
      case "batchId":
      case "prettyBatchId":
      case "slaFulfillmentDate":
      case "scanVerified":
      case "holds":
      case "releaseHoldsAt":
      case "events":
      case "marketplaceNotification":
      case "notifyMarketplace":
      case "notifyCustomer":
      case "fulfillmentDate":
      case "marketplaceFulfillmentDelayCriteria":
      case "fulfilledBy":
      case "manualFulfillmentInfo":
      case "pickSessionId":
      case "overrideAsReceived":
      case "archivedInterallyAt":
      case "trackingSyncedAt":
      case "forceResyncAt":
      case "forceResyncEvenWhenClosedIsh":
      case "forceResyncInventoryAt":
      case "integrationReferences":
      case "customerAccountsToGetRatesFor":
      case "shippingClaimIds":
      case "shippingClaimIdsByTrackingNumber":
      case "attemptedProactiveClaim":
      case "buyReturnLabel":
      case "sendLabelPurchasedNotification":
      case "labelPurchasedNotificationSent":
      case "returnShipment":
      case "returnShipmentOverrides":
      case "isSplit":
      case "shipmentPaymentOption":
      case "shipmentPaymentOptions":
      case "buyLabelErrors":
      case "buyReturnLabelErrors":
      case "labelPurchaseFailedAt":
      case "shipmentDetails":
      case "pickStatus":
      case "pickSessionItemStatuses":
      case "pickedAt":
      case "problemResolvedAt":
      case "pickStartedOn":
      case "assignedPickerId":
      case "holdsPlacedByInfo":
      case "voidedBatchId":
      case "scanVerifiedItemized":
      case "productRecommendations":
      case "overriddenAsReceivedFulfillmentOrderIds":
      case "packingParcels":
      case "itemParcelAssignments":
      case "parcelsFinalized":
      case "landedCosts":
      case "papsNumber":
      case "portOfEntry":
      case "crossingDate":
      case "csInventoryIncrementedLineItemIds":
      case "cancelledWithoutRestock":
      case "idempotencyControl":
      case "canadaPostOption":
        break;
      // Every time we add a new key to a fulfillment group, we need to consider if the field should be copied to the new fulfillment group on split/void - this check makes us think about that
      default:
        assertNever(k);
    }
  }
}

export function getFulfillmentGroupItems(
  group: FulfillmentGroup | FulfillmentGroupData,
): FulfillmentOrderLineItem[] {
  if ("fulfillmentOrders" in group) {
    return group.fulfillmentOrders.flatMap((fo) => fo.lineItems ?? []);
  }
  return group.items ?? [];
}

// Returns null if there are no pick records on the fulfillment group
export function getNumUnpickedItems(
  group: FulfillmentGroup | FulfillmentGroupData,
  options: { hideInputItems: boolean },
): number | null {
  // Only calculate unpicked items for PROBLEM pickStatus
  if (group.pickStatus !== FulfillmentGroupPickStatus.PROBLEM) {
    return null;
  }

  const rawItems = getFulfillmentGroupItems(group);
  const pickableItems = expandLineItemsToPickableItems(rawItems, options);
  const totalQuantity = pickableItems.reduce((acc, item) => {
    return acc + item.quantity;
  }, 0);

  // If no pickSessionItemStatuses, assume all items are unpicked
  if (!group.pickSessionItemStatuses) {
    return totalQuantity;
  }

  const totalPickedItems = group.pickSessionItemStatuses.reduce(
    (acc, status) => {
      return acc + status.quantityPicked;
    },
    0,
  );

  return totalQuantity - totalPickedItems;
}

export function getFulfillmentGroupShipments(
  group: FulfillmentGroup,
): ShipmentPackage[] {
  return filterTruthy([group.shipment, ...(group.additionalShipments ?? [])]);
}

export function getFulfillmentGroupShipmentsCost(
  group: FulfillmentGroup,
): number {
  return getFulfillmentGroupShipments(group).reduce((acc, shipment) => {
    return acc + Number(shipment.boughtRate?.rate ?? 0);
  }, 0);
}

export function getGlobalEOrderId(group: FulfillmentGroup): string | undefined {
  const globalEOrderIds = group.fulfillmentOrders.map(
    (fo) =>
      fo.order?.customAttributes?.find((attr) => attr.key === "GEOrderId")
        ?.value,
  );
  if (globalEOrderIds.every((id) => id === globalEOrderIds[0])) {
    return globalEOrderIds[0] ?? undefined;
  }
  return undefined;
}

/**
 * Validates that all line items in a fulfillment group have the same currency
 * and returns that currency code. Throws if currencies are mixed.
 */
export function getUniformCurrency(group: FulfillmentGroup): Currency {
  const lineItems = group.fulfillmentOrders.flatMap((fo) => fo.lineItems ?? []);

  if (lineItems.length === 0) {
    return Currency.USD;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const firstCurrency = lineItems[0]!.unitPrice.currency;

  for (const li of lineItems) {
    if (li.unitPrice.currency !== firstCurrency) {
      throw new Error(
        `Mixed currencies in fulfillment group: found ${li.unitPrice.currency} but expected ${firstCurrency}`,
      );
    }
  }

  return firstCurrency;
}

function ensureCarrierAccountId(rate: OutboundRate): OutboundRate {
  if (rate.carrier_account_id) {
    return rate;
  }
  // Any old IB rates that didn't have a carrier_account_id set will now have it
  if (rate.provider === RateProvider.INTERNATIONAL_BRIDGE) {
    return { ...rate, carrier_account_id: IB_CARRIER_ACCOUNT_ID };
  }
  return rate;
}

export function getFulfillmentGroupTrackingCodes(
  group: FulfillmentGroup,
): string[] {
  return filterTruthy([
    group.shipment?.tracker?.trackingCode,
    ...(group.additionalShipments?.map((s) => s.tracker?.trackingCode) ?? []),
  ]);
}
